import { Chip, Divider, Stack, Tooltip, Typography } from '@mui/material';
import { ReactNode } from 'react';
import {
    DateField,
    EditButton,
    Link,
    NumberField,
    ReferenceField,
    ShowButton,
    TextField,
    useRecordContext,
} from 'react-admin';

import { Supplier } from '../types';

interface SupplierAsideProps {
    link?: string;
}

const formatNumber = (value?: number) =>
    value == null
        ? '—'
        : value.toLocaleString('pt-BR', { maximumFractionDigits: 2 });

export const SupplierAside = ({ link = 'edit' }: SupplierAsideProps) => {
    const record = useRecordContext<Supplier>();
    if (!record) return null;

    return (
        <Stack ml={4} width={250} minWidth={250} spacing={2}>
            <Stack direction="row" spacing={1}>
                {link === 'edit' ? (
                    <EditButton label="Editar Fornecedor" />
                ) : (
                    <ShowButton label="Visualizar Fornecedor" />
                )}
            </Stack>

            <OperacaoInfo record={record} />
            <CompradorInfo record={record} />
            <AnaliseInfo record={record} />
            <InfoAdicionalChips record={record} />
        </Stack>
    );
};

const Section = ({
    title,
    children,
}: {
    title: string;
    children: ReactNode;
}) => (
    <Stack>
        <Typography variant="subtitle2">{title}</Typography>
        <Divider sx={{ mb: 1 }} />
        {children}
    </Stack>
);

const MetricRow = ({
    label,
    tooltip,
    children,
}: {
    label: string;
    tooltip?: string;
    children: ReactNode;
}) => (
    <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="baseline"
        gap={1}
        minHeight={24}
    >
        {tooltip ? (
            <Tooltip title={tooltip} placement="left" arrow enterDelay={300}>
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                        cursor: 'help',
                        width: 'fit-content',
                        borderBottom: '1px dotted',
                        borderColor: 'text.disabled',
                    }}
                >
                    {label}
                </Typography>
            </Tooltip>
        ) : (
            <Typography variant="body2" color="text.secondary">
                {label}
            </Typography>
        )}
        <Typography variant="body2" color="text.primary" textAlign="right">
            {children}
        </Typography>
    </Stack>
);

const InfoAdicionalChips = ({ record }: { record: Supplier }) => {
    const cfg = record.config_fornec;
    const chips = [
        record.ativo === false ? (
            <Chip key="inativo" size="small" label="Inativo" variant="outlined" />
        ) : (
            <Chip key="ativo" size="small" label="Ativo" color="success" />
        ),
        <Chip
            key="nf"
            size="small"
            label={record.consumo ? 'Consumo' : 'Revenda'}
            color={record.consumo ? 'warning' : 'default'}
            variant="outlined"
        />,
        record.dia_semana ? (
            <Chip
                key="dia"
                size="small"
                label={record.dia_semana}
                variant="outlined"
            />
        ) : null,
        cfg?.semana_par ? (
            <Chip key="par" size="small" label="Semana par" variant="outlined" />
        ) : null,
        cfg?.semana_impar ? (
            <Chip
                key="impar"
                size="small"
                label="Semana ímpar"
                variant="outlined"
            />
        ) : null,
        cfg?.produto ? (
            <Chip key="produto" size="small" label="Produto" variant="outlined" />
        ) : null,
        cfg?.servico ? (
            <Chip key="servico" size="small" label="Serviço" variant="outlined" />
        ) : null,
    ].filter(Boolean);

    if (!chips.length) return null;

    return (
        <Section title="Informações Adicionais">
            <Stack direction="row" flexWrap="wrap" gap={0.75}>
                {chips}
            </Stack>
        </Section>
    );
};

const OperacaoInfo = ({ record }: { record: Supplier }) => {
    const cfg = record.config_fornec;
    const hasMetrics =
        record.quant_skus != null ||
        cfg?.dias_tr != null ||
        cfg?.intervalo_analise != null ||
        cfg?.ic_padrao != null ||
        cfg?.pedido_minimo != null ||
        Boolean(cfg?.prazo_pagamento);

    if (!hasMetrics) return null;

    return (
        <Section title="Operação">
            {record.quant_skus != null && (
                <MetricRow label="SKUs">
                    <Link
                        to={{
                            pathname: '/scmskus',
                            search: `?filter=${encodeURIComponent(
                                JSON.stringify({ id_fornec: record.id })
                            )}`,
                        }}
                    >
                        <NumberField
                            source="quant_skus"
                            record={record}
                            options={{ useGrouping: true }}
                        />
                    </Link>
                </MetricRow>
            )}
            {cfg?.dias_tr != null && (
                <MetricRow label="TR" tooltip="Fórmula">
                    {formatNumber(cfg.dias_tr)}
                </MetricRow>
            )}
            {cfg?.intervalo_analise != null && (
                <MetricRow label="IA" tooltip="Intervalo de análise">
                    {cfg.intervalo_analise}
                </MetricRow>
            )}
            {cfg?.ic_padrao != null && (
                <MetricRow label="IC" tooltip="Índice de compra">
                    {formatNumber(cfg.ic_padrao)}
                </MetricRow>
            )}
            {cfg?.pedido_minimo != null && (
                <MetricRow label="Pedido mín.">
                    {formatNumber(cfg.pedido_minimo)}
                </MetricRow>
            )}
            {cfg?.prazo_pagamento ? (
                <MetricRow label="Prazo">{cfg.prazo_pagamento}</MetricRow>
            ) : null}
        </Section>
    );
};

const CompradorInfo = ({ record }: { record: Supplier }) => {
    if (record.id_comprador == null && !record.nome_comprador) return null;

    return (
        <Section title="Comprador">
            {record.id_comprador != null ? (
                <ReferenceField
                    source="id_comprador"
                    reference="buyers"
                    record={record}
                    link={false}
                />
            ) : (
                <TextField source="nome_comprador" record={record} />
            )}
        </Section>
    );
};

const AnaliseInfo = ({ record }: { record: Supplier }) => {
    const ultimaOc = record.config_fornec?.ultima_oc;
    if (!ultimaOc && !record.dthr_analise && !record.data_cadastro) {
        return null;
    }

    return (
        <Section title="Análise">
            {ultimaOc ? (
                <MetricRow label="Última OC">
                    <DateField
                        source="config_fornec.ultima_oc"
                        record={record}
                        options={{
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                        }}
                    />
                </MetricRow>
            ) : null}
            {record.dthr_analise ? (
                <MetricRow label="Análise">
                    <DateField
                        source="dthr_analise"
                        record={record}
                        showTime
                        options={{
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                        }}
                    />
                </MetricRow>
            ) : null}
            {record.data_cadastro ? (
                <MetricRow label="Cadastro">
                    <DateField
                        source="data_cadastro"
                        record={record}
                        options={{
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric',
                        }}
                    />
                </MetricRow>
            ) : null}
        </Section>
    );
};
